import React, { useRef, useState } from "react";
import PhoneNumberField from "../../components/FormComponents/PhoneNumberField";
import InputField from "../../components/FormComponents/InputField";
import { CircularProgress } from "@mui/material";
import { useSelector } from "react-redux";
import { Camera, RotateCcw } from "lucide-react";

const OtherDetails = ({
  formData,
  updateFormData,
  prevStep,
  onSubmit,
  handlePhoneNumberChange,
  handleCodeChange
}) => {
  const { loading } = useSelector((state) => state.Auth);
  const [preview, setPreview] = useState(formData.sender_image || null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [imageError, setImageError] = useState("");
  const [facingMode, setFacingMode] = useState("user"); // 'user' for front camera, 'environment' for back camera
  const [errors, setErrors] = useState({}); // State for error messages

  // Open the camera and stream video
  const handleTakePhoto = async () => {
    setIsCameraOpen(true);
    setImageError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          facingMode: facingMode
        } 
      });
      videoRef.current.srcObject = stream;
      videoRef.current.play();
    } catch (error) {
      console.error("Camera access error:", error);
      alert("Unable to access camera. Please allow camera permissions.");
    }
  };

  // Switch between front and back camera
  const switchCamera = async () => {
    const newFacingMode = facingMode === "user" ? "environment" : "user";
    setFacingMode(newFacingMode);
    
    // Stop current stream
    const currentStream = videoRef.current.srcObject;
    if (currentStream) {
      currentStream.getTracks().forEach(track => track.stop());
    }
    
    // Start new stream with different camera
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          facingMode: newFacingMode
        } 
      });
      videoRef.current.srcObject = stream;
      videoRef.current.play();
    } catch (error) {
      console.error("Error switching camera:", error);
      alert("Unable to switch camera. Your device might not support multiple cameras.");
    }
  };

  // Capture photo from video with natural orientation
  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    // Set canvas dimensions to match video's actual dimensions
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const context = canvas.getContext("2d");
    
    // For front camera (user), flip horizontally to make it natural
    if (facingMode === "user") {
      // Flip the image horizontally to make it look natural (not mirrored)
      context.translate(canvas.width, 0);
      context.scale(-1, 1);
    }
    
    // Draw the image
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Reset transformation for front camera
    if (facingMode === "user") {
      context.setTransform(1, 0, 0, 1, 0, 0);
    }
    
    // Convert to high quality JPEG
    const imageData = canvas.toDataURL("image/jpeg", 0.92);

    // Stop camera stream
    const stream = video.srcObject;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }

    setIsCameraOpen(false);
    setPreview(imageData);
    updateFormData({
      target: { name: "sender_image", value: imageData },
    });
    setImageError("");
  };

  // Cancel camera
  const cancelCamera = () => {
    const stream = videoRef.current.srcObject;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    setIsCameraOpen(false);
  };

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    // Sender Image validation
    if (!formData.sender_image) {
      newErrors.sender_image = "Sender image is required";
    }

    // Expected Education validation
    if (!formData.expected_education?.trim()) {
      newErrors.expected_education = "Expected education is required";
    } else if (formData.expected_education.length < 2) {
      newErrors.expected_education = "Expected education should be at least 2 characters long";
    }

    // Person to Contact Name validation
    if (!formData.person_to_contact_name?.trim()) {
      newErrors.person_to_contact_name = "Person to contact name is required";
    } else if (formData.person_to_contact_name.length < 2) {
      newErrors.person_to_contact_name = "Person to contact name should be at least 2 characters long";
    }

    // Person to Contact Phone validation
    if (!formData.person_to_contact_phone) {
      newErrors.person_to_contact_phone = "Person to contact phone number is required";
    } else if (formData.person_to_contact_phone.length < 10) {
      newErrors.person_to_contact_phone = "Please enter valid phone number for person to contact";
    }

    // Social Media validation (optional but validate if provided)
    if (formData.instagram && formData.instagram.length < 2) {
      newErrors.instagram = "Instagram username should be at least 2 characters long";
    }
    if (formData.linkedin && formData.linkedin.length < 2) {
      newErrors.linkedin = "LinkedIn username should be at least 2 characters long";
    }
    if (formData.snapchat && formData.snapchat.length < 2) {
      newErrors.snapchat = "Snapchat username should be at least 2 characters long";
    }
    if (formData.twitter && formData.twitter.length < 2) {
      newErrors.twitter = "Twitter username should be at least 2 characters long";
    }
    if (formData.facebook && formData.facebook.length < 2) {
      newErrors.facebook = "Facebook username should be at least 2 characters long";
    }

    return newErrors;
  };

  // Handle form submission with validation
  const handleSubmit = (e) => {
    e.preventDefault();
    
    const newErrors = validateForm();
    setErrors(newErrors);

    // Check if there are any errors
    if (Object.keys(newErrors).length > 0) {
      // Scroll to first error
      const firstErrorField = Object.keys(newErrors)[0];
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
      return;
    }
    
    onSubmit();
  };

  // Helper function to update form data and clear errors
  const handleUpdateFormData = (e) => {
    updateFormData(e);
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors(prev => ({ ...prev, [e.target.name]: "" }));
    }
  };

  // Helper function for phone number changes with error clearing
  const handlePhoneChangeWithValidation = (field, newPhone) => {
    handlePhoneNumberChange(field, newPhone);
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  // Helper function for country code changes with error clearing
  const handleCodeChangeWithValidation = (field, newCode) => {
    handleCodeChange(field, newCode);
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto p-4">
      <div className="max-h-[70vh] overflow-y-auto px-2">
        {/* Commented out Partner's Parents Origin Choice */}
        {/* <InputField
          label="Partner's Parents Origin Choice"
          name="partners_parents_origin_choice"
          placeholder="Enter origin"
          value={formData.partners_parents_origin_choice}
          onChange={updateFormData}
        /> */}

        {/* Expected Education */}
        <div className="mb-4">
          <InputField
            label="Expected Education"
            name="expected_education"
            placeholder="Enter expected education"
            value={formData.expected_education}
            onChange={handleUpdateFormData}
            error={errors.expected_education}
            required={true}
          />
          {errors.expected_education && <p className="text-red-500 text-sm mt-1">{errors.expected_education}</p>}
        </div>

        {/* Sender's Details */}
        <label className="block font-medium mb-2 mt-4">Sender's Details<span className="text-[14px] text-[#E72848]">*</span></label>

        {/* Sender's Image - Natural Photo */}
        <div className="mb-4">
          <label className="block text-md font-medium mb-2">
            Selfie Verification <span className="text-red-500">*</span>
          </label>

          {errors.sender_image && (
            <p className="text-red-500 text-sm mb-3">{errors.sender_image}</p>
          )}

          {/* Image preview */}
          {preview ? (
            <div className="flex flex-col items-center">
              <img
                src={preview}
                alt="Sender"
                className="w-48 h-48 object-cover rounded-lg border-4 border-white shadow-lg mb-4"
              />
              <button
                type="button"
                onClick={handleTakePhoto}
                className="px-4 py-2 bg-[#e72848] text-white rounded-md hover:bg-[#d62040] transition flex items-center gap-2 shadow-md"
              >
                <Camera size={18} /> Take New Photo
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-8 bg-gray-50">
              <Camera className="text-gray-400 mb-4" size={56} />
              <p className="text-gray-600 mb-2 text-lg font-medium text-center">
                Photo Required
              </p>
              <p className="text-gray-500 text-sm mb-6 text-center max-w-md">
                Take a clear, well-lit photo for profile verification. 
                {facingMode === "environment" ? " Use the back camera for best quality." : " Use the front camera for self-portrait."}
              </p>
              <button
                type="button"
                onClick={handleTakePhoto}
                className="px-6 py-3 bg-[#e72848] text-white rounded-lg hover:bg-[#d62040] transition flex items-center gap-2 shadow-lg font-medium"
              >
                <Camera size={20} /> Take Photo
              </button>
            </div>
          )}
        </div>

        {/* Camera Capture Modal - Improved for Natural Photos */}
        {isCameraOpen && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-90 z-50 transition-all">
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-2xl relative overflow-hidden">
              {/* Header */}
              <h3 className="text-2xl font-bold text-center text-gray-800 mb-2">
                📸 Take Your Photo
              </h3>
              <p className="text-gray-600 text-center mb-4">
                {facingMode === "user" 
                  ? "Front Camera - Photo will be mirrored for natural appearance" 
                  : "Back Camera - For highest quality photos"}
              </p>

              {/* Camera Indicator */}
              <div className="flex justify-center items-center gap-2 mb-4">
                <span className="text-sm text-gray-600">
                  {facingMode === "user" ? "Front Camera" : "Back Camera"}
                </span>
                <button
                  onClick={switchCamera}
                  className="p-2 bg-gray-200 rounded-full hover:bg-gray-300 transition"
                  title="Switch Camera"
                >
                  <RotateCcw size={16} />
                </button>
              </div>

              {/* Video Container */}
              <div className="relative rounded-xl overflow-hidden bg-black mx-auto max-w-lg">
                <video
                  ref={videoRef}
                  className={`w-full h-auto max-h-[60vh] object-contain rounded-lg ${
                    facingMode === "user" ? "scale-x-[-1]" : ""
                  }`}
                  autoPlay
                  playsInline
                  muted
                ></video>

                {/* Camera guide overlay */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-64 h-80 border-2 border-white border-dashed rounded-lg opacity-40"></div>
                </div>

                {/* Quality indicator */}
                <div className="absolute top-4 left-4 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-sm">
                  {facingMode === "environment" ? "📷 High Quality" : "🤳 Self Portrait"}
                </div>
              </div>

              {/* Hidden canvas for capture - full quality */}
              <canvas
                ref={canvasRef}
                className="hidden"
              ></canvas>

              {/* Control Buttons */}
              <div className="flex justify-center gap-4 mt-6">
                <button
                  onClick={capturePhoto}
                  className="px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg shadow-lg hover:scale-105 transition-transform duration-200 font-semibold flex items-center gap-2"
                >
                  <Camera size={20} /> Capture Photo
                </button>
                <button
                  onClick={cancelCamera}
                  className="px-8 py-3 bg-gray-200 text-gray-800 rounded-lg shadow hover:bg-gray-300 hover:scale-105 transition-transform duration-200 font-semibold"
                >
                  Cancel
                </button>
              </div>

              {/* Camera Tips */}
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-600 mb-2">
                  💡 Tips for best results:
                </p>
                <div className="flex flex-wrap justify-center gap-4 text-xs text-gray-500">
                  <span>Good lighting</span>
                  <span>Steady hands</span>
                  <span>Clear background</span>
                  <span>Face the camera</span>
                </div>
              </div>

              {/* Decorative effects */}
              <div className="absolute -top-32 -left-32 w-64 h-64 bg-blue-200 rounded-full blur-3xl opacity-30 animate-pulse"></div>
              <div className="absolute -bottom-32 -right-32 w-64 h-64 bg-pink-200 rounded-full blur-3xl opacity-30 animate-pulse"></div>
            </div>
          </div>
        )}

        {/* Commented out Sender Name */}
        {/* <InputField
          label="Name"
          name="senders_name"
          placeholder="Enter name"
          value={formData.senders_name}
          onChange={updateFormData}
        /> */}

        {/* Commented out Sender Phone */}
        {/* <div className="mb-4">
          <label className="block my-2 font-medium text-md">Phone Number</label>
          <PhoneNumberField
            value={formData.senders_phone_number}
            setPhoneNumber={(newPhone) =>
              handlePhoneNumberChange("senders_phone_number", newPhone)
            }
            code={formData.senders_country_code}
            setCode={(newCode) =>
              handleCodeChange("senders_country_code", newCode)
            }
            country={formData.senders_country}
          />
        </div> */}

        {/* Person to Contact */}
        <label className="block font-medium mb-2 mt-4">Person to Contact<span className="text-[14px] text-[#E72848]">*</span></label>
        <div className="mb-4">
          <InputField
            label="Name"
            name="person_to_contact_name"
            placeholder="Enter name"
            value={formData.person_to_contact_name}
            onChange={handleUpdateFormData}
            error={errors.person_to_contact_name}
            required={true}
          />
          {errors.person_to_contact_name && <p className="text-red-500 text-sm mt-1">{errors.person_to_contact_name}</p>}
        </div>

        <div className="mb-4">
          <label className="block my-2 font-medium text-md">Phone Number<span className="text-[14px] text-[#E72848]">*</span></label>
          <PhoneNumberField
            value={formData.person_to_contact_phone}
            setPhoneNumber={(newPhone) =>
              handlePhoneChangeWithValidation("person_to_contact_phone", newPhone)
            }
            code={formData.person_to_contact_country_code}
            setCode={(newCode) =>
              handleCodeChangeWithValidation("person_to_contact_country_code", newCode)
            }
            country={formData.person_to_contact_country}
            error={errors.person_to_contact_phone}
          />
          {errors.person_to_contact_phone && <p className="text-red-500 text-sm mt-1">{errors.person_to_contact_phone}</p>}
        </div>

        {/* Social Media */}
        <label className="block font-medium mb-2 mt-4">Social Media</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <InputField
              label="Instagram"
              name="instagram"
              placeholder="Enter Instagram username"
              value={formData.instagram}
              onChange={handleUpdateFormData}
              required={false}
              error={errors.instagram}
            />
            {errors.instagram && <p className="text-red-500 text-sm mt-1">{errors.instagram}</p>}
          </div>
          <div>
            <InputField
              label="LinkedIn"
              name="linkedin"
              placeholder="Enter LinkedIn username"
              value={formData.linkedin}
              onChange={handleUpdateFormData}
              required={false}
              error={errors.linkedin}
            />
            {errors.linkedin && <p className="text-red-500 text-sm mt-1">{errors.linkedin}</p>}
          </div>
          <div>
            <InputField
              label="Snapchat"
              name="snapchat"
              placeholder="Enter Snapchat username"
              value={formData.snapchat}
              onChange={handleUpdateFormData}
              required={false}
              error={errors.snapchat}
            />
            {errors.snapchat && <p className="text-red-500 text-sm mt-1">{errors.snapchat}</p>}
          </div>
          <div>
            <InputField
              label="Twitter"
              name="twitter"
              placeholder="Enter Twitter username"
              value={formData.twitter}
              onChange={handleUpdateFormData}
              required={false}
              error={errors.twitter}
            />
            {errors.twitter && <p className="text-red-500 text-sm mt-1">{errors.twitter}</p>}
          </div>
          <div className="sm:col-span-2">
            <InputField
              label="Facebook"
              name="facebook"
              placeholder="Enter Facebook username"
              value={formData.facebook}
              onChange={handleUpdateFormData}
              required={false}
              error={errors.facebook}
            />
            {errors.facebook && <p className="text-red-500 text-sm mt-1">{errors.facebook}</p>}
          </div>
        </div>
      </div>

      {/* Footer Buttons */}
      <div className="flex justify-center gap-3 mt-6 w-full px-2">
        <button
          type="button"
          className="flex-1 py-3 text-[#E72848] border border-[#E72848] rounded-[10px] h-[45px] font-semibold hover:bg-[#FCE4EC] transition duration-300"
          onClick={(e) => {
            e.preventDefault();
            prevStep();
          }}
        >
          Back
        </button>
        <button
          type="button"
          className="flex-1 py-3 flex justify-center items-center text-white rounded-[10px] h-[45px] font-semibold transition duration-300 bg-[#E72848] hover:bg-[#f53b58] cursor-pointer"
          onClick={handleSubmit}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "Create Profile"}
        </button>
      </div>
    </div>
  );
};

export default OtherDetails;